import React, { useState, useCallback, useMemo, useEffect, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import { FieldError, FieldInfo } from '../../../../../hybrid/wrappers';
import { initFormHandlers } from '../../../utils/formUtils';
import FromInputTitle from '../../FormFieldTitle/FormFieldTitle';
import { getConfig, getPhoneLength, usePhoneWidget } from '../helpers';
import { UserProfileContext } from '../../../../../common/utils/getContext';
import InputSwitcher from '../components/Elements/Elements';
import { getDefaultVariation, getIsCoupleVariation } from '../../helpers';
import { useWindowDimensions } from '../../../../../common/utils/windowDimensions';
import css from './FormInput.module.scss';
import classnames from 'classnames';
import { generateCompStylers } from '../../../../../common/utils/styles';
import { cleanProps, useRandom } from '../../../../../common/utils/utils';
import RecoverPass from '../components/RecoverPass/RecoverPass';
import { tToString } from '../../../../../common/components/T';
import { LangContext } from '../../../../../common/decorators/Language';
import DropdownMenu from '../../form-dropdown/components/DropdownMenu/DropdownMenu';
import ReactDOM from 'react-dom';

const Wrapper = (props) => {
    const { noWrapper, children } = props;
    if (noWrapper) {
        return <>{children}</>;
    } else {
        return <div {...props}>{children}</div>;
    }
};
const Label = (props) => {
    const { isPhone, children, refElem } = props;
    const attributes = cleanProps(props, ['refElem', 'isPhone']);
    if (isPhone) {
        return (
            <div ref={refElem} {...attributes}>
                {children}
            </div>
        );
    } else {
        return (
            <label ref={refElem} {...attributes}>
                {children}
            </label>
        );
    }
};
const FormInput = (props) => {
    const {
        autocomplete,
        noWrapper,
        attribute,
        hint,
        required,
        readonly,
        disabled,
        title,
        handlers = {},
        compStyle = {},
        value,
        type = 'input',
        setFormData = () => {},
        info,
        sender = () => {},
        errors,
        typeField,
        step,
        maxLength = 150,
        search,
        variation,
        buttons = {},
        sorting = ['search', 'social', 'input', 'password', 'copy'],
        minValue = -Infinity,
        maxValue = Infinity,
        addon,
        validationFormatter,
        placeholder,
        zIndex,
        autofocus,
        isGridInput = false,
        textBottom,
        recoverPass = false,
        insertComponentBefore,
        authShowPass,
        decimalPlaces = 2,
        reloadCountry
    } = props;

    const { translation } = useContext(LangContext) || {};
    const random = useRandom();
    const mobile = useWindowDimensions() < 768;
    const fieldId = useMemo(() => {
        return `${attribute || ''}_${random}_field`;
    }, [attribute, random]);
    const { stylers, classes } = generateCompStylers(compStyle);
    const contextData = useContext(UserProfileContext) || {};
    const sorted = [
        ...sorting,
        ...Object.keys(buttons).reduce((ac, k) => {
            if (sorting.includes(k)) return ac;
            return [...ac, k];
        }, [])
    ];
    const variationStyle = variation || getDefaultVariation(contextData, title);
    const [val, setVal] = useState(value || '');
    const validatedValue = useMemo(() => tToString(value, translation), [value, translation]);
    const inputRef = useRef();

    const [focusedState, setFocused] = useState(false);
    const focused = props?.customFocus === undefined ? focusedState : props?.customFocus;

    const changeHandlerPhone = useCallback(
        (v, selectedCountryData) => {
            const cleanValue = v ? v.replace(/\D/g, '') : '';
            const phoneLength = getPhoneLength(selectedCountryData);
            const formResult = cleanValue ? `${selectedCountryData?.phone_code?.replace(/\D/g, '')}${cleanValue}` : '';
            setFormData(attribute, cleanValue ? `+${formResult.slice(0, phoneLength)}` : '');
            setVal(cleanValue ? `+${formResult.slice(0, phoneLength)}` : '');
        },
        [attribute, setFormData]
    );

    const [showPass, setShowPass] = useState(authShowPass);
    const isPhone = type === 'phone-widget';
    const { phoneDropDown, selectedCountry } = usePhoneWidget(
        isPhone,
        value,
        val,
        attribute,
        changeHandlerPhone,
        compStyle,
        inputRef,
        reloadCountry
    );

    const changeHandler = useCallback(
        (v) => {
            const targetValue = typeof v?.target?.value === 'string' ? v?.target?.value : typeof v === 'string' ? v : '';
            if (type === 'password' && authShowPass && targetValue?.length === 1) {
                setShowPass(false);
            }
            if (!disabled) {
                if (isPhone) {
                    changeHandlerPhone(v?.target?.value, selectedCountry);
                } else if (typeField === 'number' && !mobile) {
                    const regexp = new RegExp(`^(-?\\d+(\\.\\d{0,${decimalPlaces}})?|\\.\\d{1,${decimalPlaces}})$`);
                    if (regexp.test(targetValue) || (targetValue === '-' && +minValue < 0) || targetValue === '') {
                        if (isGridInput) {
                            handlers?.onChange(targetValue);
                        } else {
                            setFormData(attribute, targetValue);
                        }
                        setVal(targetValue);
                    } else {
                        v.preventDefault();
                    }
                } else {
                    let valCor =
                        step === 1 && targetValue && !isNaN(parseInt(`${targetValue}`))
                            ? parseInt(`${targetValue}`.replace(/[^0-9]/g, ''))
                            : step === 1 && targetValue
                            ? ''
                            : targetValue.length > maxLength
                            ? targetValue.substr(0, maxLength - 1)
                            : targetValue;
                    if (validationFormatter) {
                        valCor = validationFormatter(valCor);
                    }
                    if ((search?.type === 'onInput' && handlers?.onChange) || isGridInput) {
                        handlers.onChange(valCor);
                    } else if (handlers?.onChange) {
                        setFormData(attribute, valCor);
                        handlers.onChange(valCor, v);
                    } else {
                        setFormData(attribute, valCor);
                    }
                    setVal(valCor);
                }
            }
        },
        [
            disabled,
            type,
            typeField,
            mobile,
            setFormData,
            attribute,
            step,
            maxLength,
            validationFormatter,
            search?.type,
            handlers,
            isGridInput,
            isPhone,
            selectedCountry,
            changeHandlerPhone,
            authShowPass,
            decimalPlaces
        ]
    );
    const pressHandler = useCallback(
        (e) => {
            if (search?.type === 'onInput' && e.key === 'Enter') {
                handlers?.onKeyPress && handlers?.onKeyPress(e);
                e.preventDefault();
            } else if (e.key === 'Enter') {
                sender(e);
            }
        },
        [handlers, search?.type, sender]
    );
    useEffect(() => {
        setVal(validatedValue || '');
    }, [setVal, validatedValue]);
    const initHandlers = useMemo(
        () => initFormHandlers(handlers, attribute, val, type, errors, setFocused),
        [handlers, attribute, val, type, errors]
    );

    const minInputWidth = useMemo(() => {
        if (!mobile || !addon) return false;
        if (val) {
            return { minWidth: `${val?.length * 8}px` };
        } else if (placeholder?.length) {
            return { minWidth: `${placeholder?.length * 8}px` };
        }
    }, [addon, mobile, placeholder?.length, val]);
    const componentConfig = useMemo(() => {
        return getConfig(
            autocomplete,
            attribute,
            required,
            typeField,
            val,
            maxLength,
            step,
            minValue,
            readonly,
            initHandlers,
            type,
            selectedCountry,
            changeHandler,
            pressHandler,
            showPass,
            disabled,
            fieldId,
            autofocus,
            mobile,
            maxValue
        );
    }, [
        autocomplete,
        attribute,
        required,
        typeField,
        val,
        maxLength,
        step,
        minValue,
        readonly,
        initHandlers,
        type,
        selectedCountry,
        changeHandler,
        pressHandler,
        showPass,
        disabled,
        fieldId,
        autofocus,
        mobile,
        maxValue
    ]);
    const pickerElement = useRef();
    return (
        <Wrapper
            noWrapper={noWrapper}
            style={stylers.wrapper}
            className={classnames(css.formGroup, classes.wrapper, css[variationStyle])}
        >
            {!!title && !getIsCoupleVariation(variationStyle) ? (
                <FromInputTitle
                    id={componentConfig?.id}
                    errors={errors}
                    title={title}
                    compStyle={compStyle}
                    hint={hint}
                    required={required}
                    variationStyle={variationStyle}
                    focused={focused}
                    disabled={disabled}
                    type={type}
                />
            ) : null}
            <Label
                isPhone={isPhone}
                refElem={pickerElement}
                onClick={
                    isPhone || insertComponentBefore
                        ? (e) => {
                              inputRef?.current?.focus();
                              initHandlers?.onClick && initHandlers.onClick(e);
                          }
                        : initHandlers?.onClick
                }
                htmlFor={isPhone ? undefined : props?.customFocus ? `${componentConfig?.id}_disable` : componentConfig?.id}
                className={classnames(
                    css[`${variationStyle}--formControl`],
                    { [css['no-border']]: stylers.input?.border === 'none' },
                    classes.input || '',
                    errors && errors.length
                        ? [css[`${variationStyle}--formControl--error`], classes.inputError]
                        : focused
                        ? [css[`${variationStyle}--formControl--focus`], classes.inputFocus]
                        : '',
                    disabled && !errors?.length ? [css['formControl--disabled'], classes.inputDisabled] : ''
                )}
                style={{
                    ...(zIndex ? { zIndex } : {}),
                    ...stylers.input,
                    ...(errors && errors.length
                        ? stylers.inputError
                        : {
                              ...(focused ? stylers.inputFocus : {}),
                              ...(disabled ? stylers.inputDisabled : {})
                          })
                }}
            >
                <div style={stylers.content} className={classnames(css.formControlContent, classes.content)}>
                    {sorted.map((keyword) => (
                        <InputSwitcher
                            {...props}
                            minInputWidth={minInputWidth}
                            key={`${componentConfig?.id}_${keyword}`}
                            keyword={keyword}
                            changeHandler={changeHandler}
                            variationStyle={variationStyle}
                            val={val}
                            focused={focused}
                            componentConfig={componentConfig}
                            setShowPass={setShowPass}
                            showPass={showPass}
                            inputRef={inputRef}
                            selectedCountry={selectedCountry}
                            phoneDropDown={phoneDropDown}
                        />
                    ))}
                </div>
            </Label>
            {recoverPass ? <RecoverPass /> : null}
            {!!info && <FieldInfo compStyle={compStyle?.info}>{info}</FieldInfo>}
            {!!textBottom && (
                <div style={stylers.textBottom} className={classnames(css.textBottom)}>
                    {textBottom}
                </div>
            )}
            {errors && errors.length
                ? errors.map((err, idx) => (
                      <FieldError compStyle={compStyle?.error} key={`${attribute}${idx}`}>
                          {err}
                      </FieldError>
                  ))
                : null}
            {isPhone && phoneDropDown.isOpened
                ? ReactDOM.createPortal(<DropdownMenu {...phoneDropDown.props} pickerElement={pickerElement} />, document?.body)
                : null}
        </Wrapper>
    );
};
FormInput.propTypes = {
    noWrapper: PropTypes.bool,
    attribute: PropTypes.string,
    hint: PropTypes.node,
    placeholder: PropTypes.node,
    required: PropTypes.bool,
    readonly: PropTypes.bool,
    disabled: PropTypes.bool,
    title: PropTypes.object,
    handlers: PropTypes.object,
    compStyle: PropTypes.object,
    value: PropTypes.string,
    type: PropTypes.string,
    setFormData: PropTypes.func,
    info: PropTypes.node,
    sender: PropTypes.func,
    errors: PropTypes.array,
    message: PropTypes.object,
    typeField: PropTypes.string,
    step: PropTypes.number,
    maxLength: PropTypes.number,
    search: PropTypes.object,
    addon: PropTypes.node,
    copy: PropTypes.object,
    tooltip: PropTypes.node,
    variation: PropTypes.string,
    customData: PropTypes.object,
    buttons: PropTypes.object,
    sorting: PropTypes.array,
    minValue: PropTypes.number
};
export default FormInput;
